import React from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from '../../util/reactIntl';
import { SUPPORTED_LOCALES, normalizeLocale, writeLocaleCookie } from '../../util/locale';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

import css from './LanguageSwitcher.module.css';

const LABELS = { en: 'EN', de: 'DE' };

/**
 * Small DE | EN switch. The choice is remembered in a cookie (so the server
 * renders the next page in that language) and, for signed-in users, in the
 * profile (so it follows them to other devices - see ducks/auth.duck.js).
 * The page is reloaded so that server and browser agree on the language.
 */
const LanguageSwitcher = props => {
  const { className } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const current = normalizeLocale(intl.locale) || 'en';

  const choose = async locale => {
    if (locale === current) {
      return;
    }
    writeLocaleCookie(locale);
    if (isAuthenticated) {
      try {
        await dispatch(updateProfile({ publicData: { locale } }));
      } catch (e) {
        // The cookie already carries the choice on this device; the profile copy is a bonus.
      }
    }
    window.location.reload();
  };

  return (
    <div
      className={classNames(css.root, className)}
      role="group"
      aria-label={intl.formatMessage({ id: 'LanguageSwitcher.label' })}
    >
      {SUPPORTED_LOCALES.map((locale, index) => (
        <React.Fragment key={locale}>
          {index > 0 ? <span className={css.divider}>|</span> : null}
          <button
            type="button"
            lang={locale}
            className={classNames(css.option, { [css.active]: locale === current })}
            aria-pressed={locale === current}
            onClick={() => choose(locale)}
          >
            {LABELS[locale]}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
